import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import moment, { Moment } from "moment-timezone";
import { twMerge } from "tailwind-merge";
import { AppointmentSummary, MemberSettings } from "../../../../hooks/agenda";
import NoTranslate from "../../../atoms/NoTranslate";
import {
  QUARTER_MINUTES,
  dayBounds,
  hourLabel,
  isWithinHours,
  layoutDay,
  participantNames,
  rangeLabel,
  visibleHours,
  wallClockAt,
  wallMinutes,
} from "./agendaDates";
import { PENDING_HATCH, paymentViewOf } from "./agendaPayment";

const QUARTER_PX = 16;
const QUARTERS_PER_HOUR = 60 / QUARTER_MINUTES;
const HOUR_PX = QUARTER_PX * QUARTERS_PER_HOUR;
const FOCUS_HOUR = 9;

interface WeekGridProps {
  days: Moment[];
  appointments: AppointmentSummary[];
  canCreate: boolean;
  showMembers: boolean;
  /** Of the person shown; undefined without one. */
  availability?: MemberSettings | null;
  onCreate: (start: number) => void;
  onOpen: (appointment: AppointmentSummary) => void;
}

const WeekGrid: React.FC<WeekGridProps> = ({
  days,
  appointments,
  canCreate,
  showMembers,
  availability,
  onCreate,
  onOpen,
}) => {
  const { t } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, []);

  const { startHour, endHour, openingHour } = useMemo(
    () => visibleHours(appointments, days, availability),
    [appointments, days, availability]
  );
  const firstMinute = startHour * 60;
  const quarterCount = (endHour - startHour) * QUARTERS_PER_HOUR;
  const hours = Array.from(
    { length: endHour - startHour },
    (_, i) => startHour + i
  );
  const quarterMinutes = (quarter: number) =>
    firstMinute + quarter * QUARTER_MINUTES;

  const cellStarts = useMemo(
    () =>
      days.map((day) =>
        Array.from({ length: quarterCount }, (_, quarter) => {
          const minutes = startHour * 60 + quarter * QUARTER_MINUTES;
          const start = wallClockAt(day, minutes);
          return wallMinutes(start) === minutes ? start : null;
        })
      ),
    [days, startHour, quarterCount]
  );

  const reachableQuarter = (day: number, quarter: number, step: 1 | -1) => {
    const from = Math.max(0, Math.min(quarterCount - 1, quarter));
    for (const direction of [step, -step]) {
      for (let q = from; q >= 0 && q < quarterCount; q += direction) {
        if (cellStarts[day]?.[q] != null) return q;
      }
    }
    return from;
  };

  const todayIndex = days.findIndex((d) => d.isSame(now, "day"));
  const [focus, setFocus] = useState(() => ({
    day: Math.max(0, todayIndex),
    minutes: FOCUS_HOUR * 60,
  }));
  const focusDay = Math.max(0, Math.min(days.length - 1, focus.day));
  const focusQuarter = reachableQuarter(
    focusDay,
    Math.floor((focus.minutes - firstMinute) / QUARTER_MINUTES),
    1
  );

  const periodKey = `${days[0]?.valueOf()}-${days.length}`;
  useEffect(() => {
    setFocus({ day: Math.max(0, todayIndex), minutes: FOCUS_HOUR * 60 });
  }, [periodKey, todayIndex]);

  // Until the person scrolls, the view follows the opening hour as appointments load;
  // once they have, the hour on top stays put when hours appear above it.
  const scrolled = useRef<{ period: string; autoTop: number | null }>({
    period: "",
    autoTop: null,
  });
  const scrolledStartHour = useRef(startHour);
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const { period, autoTop } = scrolled.current;
    if (period === periodKey && scroller.scrollTop !== autoTop) {
      scroller.scrollTop += (scrolledStartHour.current - startHour) * HOUR_PX;
      scrolled.current = { period, autoTop: null };
    } else {
      scroller.scrollTop = (openingHour - startHour) * HOUR_PX;
      scrolled.current = { period: periodKey, autoTop: scroller.scrollTop };
    }
    scrolledStartHour.current = startHour;
  }, [periodKey, startHour, openingHour]);

  const focusCell = (day: number, quarter: number, step: 1 | -1) => {
    const nextDay = Math.max(0, Math.min(days.length - 1, day));
    const nextQuarter = reachableQuarter(nextDay, quarter, step);
    setFocus({ day: nextDay, minutes: quarterMinutes(nextQuarter) });
    gridRef.current
      ?.querySelector<HTMLButtonElement>(
        `[data-cell="${nextDay}-${nextQuarter}"]`
      )
      ?.focus();
  };

  const onCellKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    day: number,
    quarter: number
  ) => {
    const moves: Record<string, [number, number, 1 | -1]> = {
      ArrowUp: [day, quarter - 1, -1],
      ArrowDown: [day, quarter + 1, 1],
      ArrowLeft: [day - 1, quarter, 1],
      ArrowRight: [day + 1, quarter, 1],
      PageUp: [day, quarter - QUARTERS_PER_HOUR, -1],
      PageDown: [day, quarter + QUARTERS_PER_HOUR, 1],
      Home: [day, 0, 1],
      End: [day, quarterCount - 1, -1],
    };
    const target = moves[event.key];
    if (!target) return;
    event.preventDefault();
    focusCell(...target);
  };

  const offsetOf = (ms: number, dayEnd: number) =>
    (((ms >= dayEnd ? endHour * 60 : wallMinutes(ms)) - firstMinute) /
      QUARTER_MINUTES) *
    QUARTER_PX;

  const isWeek = days.length > 1;
  const gridColumns =
    days.length === 1
      ? "grid-cols-[3.5rem_1fr]"
      : "grid-cols-[3.5rem_repeat(7,minmax(0,1fr))]";

  return (
    <div
      ref={scrollRef}
      data-testid="agenda-grid-scroller"
      className="max-h-[70vh] min-w-0 overflow-y-auto"
    >
      <div
        className={twMerge(
          "sticky top-0 z-30 grid border-b border-gray-200 bg-white",
          gridColumns
        )}
      >
        <div aria-hidden="true" />
        {days.map((day) => {
          const isToday = day.isSame(now, "day");
          return (
            <div
              key={day.valueOf()}
              className="min-w-0 border-l border-gray-100 px-1 py-2 text-center"
            >
              <div
                className={twMerge(
                  "truncate text-xs text-gray-500",
                  isToday && "font-medium text-blue-700"
                )}
              >
                {moment.localeData().weekdaysShort(day)}
              </div>
              <div
                className={twMerge(
                  "mx-auto mt-0.5 flex h-7 w-7 items-center justify-center rounded-full text-sm font-semibold tabular-nums text-gray-900",
                  isToday && "bg-blue-600 text-white"
                )}
              >
                {day.date()}
              </div>
            </div>
          );
        })}
      </div>

      <div
        ref={gridRef}
        role="group"
        aria-label={t("agenda.grid.label") as string}
        className={twMerge("grid", gridColumns)}
      >
        <div aria-hidden="true" className="relative">
          {hours.map((hour, i) => (
            <div key={hour} className="relative" style={{ height: HOUR_PX }}>
              {i > 0 && (
                <span className="absolute -top-2 right-2 text-xs tabular-nums text-gray-400">
                  {hourLabel(hour)}
                </span>
              )}
            </div>
          ))}
        </div>

        {days.map((day, dayIndex) => {
          const { dayStart, dayEnd } = dayBounds(day, startHour, endHour);
          const positioned = layoutDay(appointments, dayStart, dayEnd);
          const nowOffset =
            now >= dayStart && now < dayEnd ? offsetOf(now, dayEnd) : null;

          return (
            <div
              key={day.valueOf()}
              className="relative min-w-0 border-l border-gray-100"
            >
              {Array.from({ length: quarterCount }, (_, quarter) => {
                const start = cellStarts[dayIndex][quarter];
                const working =
                  start !== null && isWithinHours(start, availability);
                const cellClass = twMerge(
                  "block h-4 w-full",
                  quarter > 0 &&
                    quarter % QUARTERS_PER_HOUR === 0 &&
                    "border-t border-gray-200",
                  quarter % QUARTERS_PER_HOUR === QUARTERS_PER_HOUR / 2 &&
                    "border-t border-dashed border-gray-100",
                  working ? "bg-white" : "bg-gray-50"
                );
                if (!canCreate || start === null) {
                  return <div key={quarter} className={cellClass} />;
                }
                const isFocusTarget =
                  focusDay === dayIndex && focusQuarter === quarter;
                return (
                  <button
                    key={quarter}
                    type="button"
                    data-cell={`${dayIndex}-${quarter}`}
                    tabIndex={isFocusTarget ? 0 : -1}
                    aria-label={
                      t("agenda.grid.createAt", {
                        date: moment(start).format("LLLL"),
                      }) as string
                    }
                    onClick={() => onCreate(start)}
                    onFocus={() =>
                      setFocus({
                        day: dayIndex,
                        minutes: quarterMinutes(quarter),
                      })
                    }
                    onKeyDown={(e) => onCellKeyDown(e, dayIndex, quarter)}
                    className={twMerge(
                      cellClass,
                      "hover:bg-blue-50 focus:outline-none focus-visible:bg-blue-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500"
                    )}
                  />
                );
              })}

              {nowOffset !== null && (
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 z-20 flex -translate-y-1/2 items-center"
                  style={{ top: nowOffset }}
                >
                  <span className="-ml-1 h-2 w-2 shrink-0 rounded-full bg-blue-600" />
                  <span className="h-0.5 flex-1 bg-blue-600" />
                </div>
              )}

              {positioned.map(
                ({ appointment, start, end, column, columns }) => {
                  const top = offsetOf(start, dayEnd);
                  const height = Math.max(
                    QUARTER_PX,
                    offsetOf(end, dayEnd) - top
                  );
                  const members = showMembers
                    ? participantNames(appointment)
                    : "";
                  const timeFormat = moment(appointment.start).isSame(
                    appointment.end - 1,
                    "day"
                  )
                    ? "LT"
                    : "ddd LT";
                  const timeRange = rangeLabel(
                    t,
                    "time",
                    moment(appointment.start).format(timeFormat),
                    moment(appointment.end).format(timeFormat)
                  );
                  const isPast = appointment.end <= now;
                  const payment = paymentViewOf(appointment);
                  const isPending = payment === "pending";
                  return (
                    <button
                      key={appointment.id}
                      type="button"
                      onClick={() => onOpen(appointment)}
                      aria-label={[
                        t("agenda.grid.openAppointment", {
                          title: appointment.title,
                          date: moment(appointment.start).format("LLLL"),
                        }),
                        payment && t(`agenda.payment.${payment}`),
                      ]
                        .filter(Boolean)
                        .join(", ")}
                      className={twMerge(
                        "absolute z-10 flex flex-col overflow-hidden rounded-md border border-l-4 border-blue-200 border-l-blue-600 bg-blue-50 px-1.5 py-0.5 text-left text-xs leading-4 text-blue-900 transition-colors hover:bg-blue-100 hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1",
                        isWeek && "border-l-2 px-1 sm:border-l-4 sm:px-1.5",
                        isPast &&
                          "border-gray-200 border-l-gray-400 bg-gray-50 text-gray-700 hover:bg-gray-100",
                        isPending &&
                          "border-amber-200 border-l-amber-500 bg-amber-50 text-amber-900 hover:bg-amber-100",
                        height < QUARTER_PX * 2 && "py-0"
                      )}
                      style={{
                        top,
                        height: height - 1,
                        left: `calc(${(column / columns) * 100}% + 2px)`,
                        width: `calc(${100 / columns}% - 4px)`,
                      }}
                    >
                      {isPending && (
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute inset-0 text-amber-200"
                          style={PENDING_HATCH}
                        />
                      )}
                      <span
                        className={twMerge(
                          "relative truncate font-semibold",
                          isWeek && columns > 1 && "hidden sm:block"
                        )}
                      >
                        <NoTranslate>{appointment.title}</NoTranslate>
                      </span>
                      {(isPending || payment === "due") &&
                        height >= QUARTER_PX * 2 && (
                          <span
                            className={twMerge(
                              "relative truncate font-medium text-amber-700",
                              isWeek && "hidden sm:block"
                            )}
                          >
                            {t(`agenda.payment.${payment}`)}
                          </span>
                        )}
                      {height >= QUARTER_PX * 3 && (
                        <span
                          className={twMerge(
                            "relative truncate tabular-nums text-blue-700",
                            isPast && "text-gray-500",
                            isPending && "text-amber-800",
                            isWeek && "hidden sm:block"
                          )}
                        >
                          {timeRange}
                        </span>
                      )}
                      {members && height >= QUARTER_PX * 4 && (
                        <span
                          className={twMerge(
                            "relative truncate text-blue-700",
                            isPast && "text-gray-500",
                            isPending && "text-amber-800",
                            isWeek && "hidden sm:block"
                          )}
                        >
                          <NoTranslate>{members}</NoTranslate>
                        </span>
                      )}
                    </button>
                  );
                }
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WeekGrid;
